import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  Button,
  Link,
  Checkbox,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import api from "../../utils/api";
import { loadingAlert, closeAlert } from "../../utils/swalConfig";

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString();
};

const TransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("reference");
  const [users, setUsers] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  const fetchTransactions = async () => {
    try {
      loadingAlert("Loading transactions...");
      const token = localStorage.getItem("jwt");
      const response = await api.get("/admin/transactions", {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter out bank transactions - only show crypto transactions
      const cryptoTransactions = (response.data.data.transactions || []).filter(
        (t) => t.method !== "bank"
      );
      setTransactions(cryptoTransactions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch transactions");
    } finally {
      closeAlert();
      setLoading(false);
    }
  };

  const handleStatusChange = async (transactionId, newStatus) => {
    try {
      const result = await Swal.fire({
        title: "Confirm Status Change",
        text: `Are you sure you want to mark this transaction as ${newStatus}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, update it!",
      });

      if (!result.isConfirmed) return;

      setUpdatingStatus(transactionId);
      const token = localStorage.getItem("jwt");

      await api.patch(
        `/admin/transactions/${transactionId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await Swal.fire({
        title: "Updated!",
        text: "Transaction status has been updated.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      await fetchTransactions();
    } catch (err) {
      Swal.fire({
        title: "Error!",
        text: err.response?.data?.message || "Failed to update status",
        icon: "error",
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const nonPendingIds = paginatedTransactions
        .filter((t) => t.status !== "pending")
        .map((t) => t._id);
      setSelectedTransactions(nonPendingIds);
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleSelect = (id, status) => {
    if (status === "pending") {
      toast.warning("Cannot select pending transactions for deletion");
      return;
    }
    setSelectedTransactions((prev) =>
      prev.includes(id) ? prev.filter((tId) => tId !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    try {
      const result = await Swal.fire({
        title: "Delete Transactions",
        text: "This will permanently delete the selected completed transactions. This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete them!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      const token = localStorage.getItem("jwt");
      await api.delete("/admin/transactions/bulk", {
        headers: { Authorization: `Bearer ${token}` },
        data: { transactionIds: selectedTransactions },
      });

      toast.success("Transactions deleted successfully");
      setSelectedTransactions([]);
      fetchTransactions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete transactions"
      );
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true;
    return transaction._id?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderTransactionHash = (hash) => {
    if (!hash) return "No hash provided";

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton size="small" onClick={() => handleCopy(hash)}>
          <CopyIcon fontSize="small" />
        </IconButton>
        <Link
          href={`https://tronscan.org/#/transaction/${hash}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "0.875rem" }}
        >
          {`${hash.substring(0, 10)}...`}
        </Link>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Crypto Transactions ({filteredTransactions.length})
        </Typography>

        <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            sx={{ minWidth: 300 }}
            size="small"
            variant="outlined"
            placeholder="Search by transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {selectedTransactions.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            sx={{ mb: 2 }}
          >
            Delete Selected ({selectedTransactions.length})
          </Button>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    onChange={handleSelectAll}
                    checked={
                      selectedTransactions.length ===
                      paginatedTransactions.filter(
                        (t) => t.status !== "pending"
                      ).length
                    }
                    indeterminate={
                      selectedTransactions.length > 0 &&
                      selectedTransactions.length <
                        paginatedTransactions.filter(
                          (t) => t.status !== "pending"
                        ).length
                    }
                  />
                </TableCell>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Network</TableCell>
                <TableCell>Crypto Amount</TableCell>
                <TableCell>NGN Amount</TableCell>
                <TableCell>Wallet Address</TableCell>
                <TableCell>Transaction Hash</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((transaction) => (
                <TableRow key={transaction._id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTransactions.includes(transaction._id)}
                      onChange={() =>
                        handleSelect(transaction._id, transaction.status)
                      }
                      disabled={transaction.status === "pending"}
                    />
                  </TableCell>
                  <TableCell>{transaction._id}</TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type}
                      color={
                        transaction.type === "deposit" ? "success" : "primary"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{transaction.network || "N/A"}</TableCell>
                  <TableCell>
                    {transaction.cryptoAmount ? (
                      <Box>
                        <Typography variant="body2">
                          {Number(transaction.cryptoAmount).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Rate: ₦
                          {Number(transaction.exchangeRate).toLocaleString()}/$
                        </Typography>
                      </Box>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ₦{(Number(transaction.amount) || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.walletAddress ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(transaction.walletAddress)}
                          title="Copy address"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {`${transaction.walletAddress.substring(
                            0,
                            8
                          )}...${transaction.walletAddress.substring(
                            transaction.walletAddress.length - 8
                          )}`}
                        </Typography>
                      </Box>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {transaction.transactionHash ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopy(transaction.transactionHash)
                          }
                          title="Copy hash"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <Link
                          href={`https://tronscan.org/#/transaction/${transaction.transactionHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {`${transaction.transactionHash.substring(
                            0,
                            8
                          )}...${transaction.transactionHash.substring(
                            transaction.transactionHash.length - 8
                          )}`}
                        </Link>
                      </Box>
                    ) : (
                      "null"
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.status}
                      color={
                        transaction.status === "successful"
                          ? "success"
                          : transaction.status === "pending"
                          ? "warning"
                          : "error"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(transaction.createdAt)}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={transaction.status}
                      onChange={(e) =>
                        handleStatusChange(transaction._id, e.target.value)
                      }
                      disabled={updatingStatus === transaction._id}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="successful">Successful</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Box>
    </Box>
  );
};

export default TransactionManagement;
